import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  BarChart3,
  LogOut,
  Sun,
  Moon,
} from 'lucide-react';

const AdminSidebar = ({ sidebarOpen, setSidebarOpen, isDesktop }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  const c = {
    bg:              isDark ? '#141414' : '#ffffff',
    border:          isDark ? '#2a2a2a' : '#e5e7eb',
    text:            isDark ? '#f1f5f9' : '#111827',
    textMuted:       isDark ? '#9ca3af' : '#6b7280',
    navActiveBg:     isDark ? '#2a2a2a' : '#f3f4f6',
    navActiveColor:  isDark ? '#f1f5f9' : '#111827',
    navInactiveColor:isDark ? '#9ca3af' : '#6b7280',
    hoverBg:         isDark ? 'rgb(60, 30, 10)' : 'rgb(253, 246, 235)',
    hoverColor:      'rgb(234, 88, 12)',
    closeBtnBg:      isDark ? '#2a2a2a' : 'white',
    closeBtnBorder:  isDark ? '#444' : '#e5e7eb',
    closeBtnColor:   isDark ? '#f1f5f9' : '#111827',
    toggleBorder:    isDark ? '#2a2a2a' : '#e5e7eb',
    toggleColor:     isDark ? '#fbbf24' : '#6366f1',
    toggleHoverBg:   isDark ? '#2a2a2a' : '#f3f4f6',
  };

  const styles = {
    sidebar: {
      width: '100%',
      maxWidth: '256px',
      backgroundColor: c.bg,
      borderRight: `1px solid ${c.border}`,
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      position: 'relative',
      zIndex: 1000,
      overflowY: 'auto',
      transition: 'background-color 0.2s ease, border-color 0.2s ease',
    },
    logoSection: {
      padding: '24px',
      borderBottom: `1px solid ${c.border}`
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    logoImage: {
      height: '36px',
      width: 'auto',
      objectFit: 'contain',
    },
    accountSection: {
      padding: '16px 24px',
      borderBottom: `1px solid ${c.border}`
    },
    roleText: {
      fontSize: '12px',
      color: c.textMuted,
    },
    nav: {
      flex: 1,
      padding: '8px 16px',
      overflowY: 'auto'
    },
    navSection: {
      marginBottom: '24px'
    },
    sectionTitle: {
      padding: '0 12px',
      marginBottom: '8px',
      fontSize: '12px',
      fontWeight: '600',
      color: c.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    navList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    navItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 12px',
      fontSize: '14px',
      fontWeight: '500',
      borderRadius: '8px',
      textDecoration: 'none',
      transition: 'all 0.2s ease',
      cursor: 'pointer'
    },
    navItemActive: {
      color: c.navActiveColor,
      backgroundColor: c.navActiveBg,
    },
    navItemInactive: {
      color: c.navInactiveColor,
      backgroundColor: 'transparent'
    },
    navIcon: {
      width: '16px',
      height: '16px',
      marginRight: '12px',
      flexShrink: 0,
    },
    bottomSection: {
      padding: '16px',
      borderTop: `1px solid ${c.border}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    }
  };

  const navigationItems = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      href: '/admin',
      icon: BarChart3,
      description: 'View metrics by brand or global'
    },
  ];

  const handleNavClick = () => {
    if (!isDesktop && setSidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const handleNavItemHover = (e) => {
    const el = e.currentTarget;
    if (el.dataset.active !== 'true') {
      el.style.color = c.hoverColor;
      el.style.backgroundColor = c.hoverBg;
    }
  };

  const handleNavItemLeave = (e) => {
    const el = e.currentTarget;
    if (el.dataset.active !== 'true') {
      el.style.color = c.navInactiveColor;
      el.style.backgroundColor = 'transparent';
    }
  };

  return (
    <div style={styles.sidebar}>
      {/* Close button for mobile/tablet */}
      {!isDesktop && sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'absolute',
            top: 18,
            right: 18,
            zIndex: 2002,
            background: c.closeBtnBg,
            border: `1.5px solid ${c.closeBtnBorder}`,
            color: c.closeBtnColor,
            borderRadius: 8,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
          aria-label="Close sidebar"
        >
          ×
        </button>
      )}

      {/* Logo */}
      <div style={styles.logoSection}>
        <div style={styles.logoContainer}>
          <img src="/shopOS_Brand_Logo.png" alt="shopOS Logo" style={styles.logoImage} />
        </div>
      </div>

      {/* Role */}
      <div style={styles.accountSection}>
        <span style={styles.roleText}>Role: Super Admin</span>
      </div>

      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navSection}>
          <h4 style={styles.sectionTitle}>Main Navigation</h4>
          <ul style={styles.navList}>
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <li key={item.id}>
                  <Link
                    to={item.href}
                    style={{
                      ...styles.navItem,
                      ...(isActive ? styles.navItemActive : styles.navItemInactive),
                    }}
                    title={item.description}
                    data-active={isActive}
                    onMouseEnter={handleNavItemHover}
                    onMouseLeave={handleNavItemLeave}
                    onClick={handleNavClick}
                  >
                    <Icon style={styles.navIcon} />
                    {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Bottom: theme toggle + logout */}
      <div style={styles.bottomSection}>
        <button
          onClick={toggleTheme}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '8px 12px',
            background: 'none',
            border: `1px solid ${c.toggleBorder}`,
            borderRadius: '8px',
            cursor: 'pointer',
            color: c.textMuted,
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = c.toggleHoverBg; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          {isDark
            ? <Sun style={{ width: 16, height: 16, color: '#fbbf24', flexShrink: 0 }} />
            : <Moon style={{ width: 16, height: 16, color: '#6366f1', flexShrink: 0 }} />
          }
          {isDark ? 'Light Mode' : 'Dark Mode'}
        </button>

        <button
          onClick={logout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '8px 12px',
            background: 'none',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            color: c.navInactiveColor,
            fontSize: '14px',
            fontWeight: '500',
            textAlign: 'left',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = c.hoverColor;
            e.currentTarget.style.backgroundColor = c.hoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = c.navInactiveColor;
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <LogOut style={styles.navIcon} />
          Logout
        </button>
      </div>
    </div>
  );
};

export default AdminSidebar;
